import { useState } from 'react';
import { RoleDeniedError } from './api';
import { labels } from './labels';

/**
 * Shows why a guarded write failed. `run(write)` clears the last message, awaits the write, and
 * returns `true` when it worked. When it throws, the backend's own message (`lib/api.ts`'s
 * `postJson`) lands in `notice`, and the caller skips its success path and keeps its state. A `403`
 * (`RoleDeniedError`) shows the role text alone, with no "That did not work" before it.
 */
export function useActionError() {
  const [text, setText] = useState<string | null>(null);

  async function run(write: () => Promise<unknown>): Promise<boolean> {
    setText(null);
    try {
      await write();
      return true;
    } catch (error) {
      if (error instanceof RoleDeniedError) setText(labels.roleDenied);
      else setText(labels.actionFailed(error instanceof Error ? error.message : String(error)));
      return false;
    }
  }

  const notice = text === null ? null : (
    <p className="notice notice--crit" role="alert">
      {text}
    </p>
  );

  return { run, notice, clear: () => setText(null) };
}
